import { Fab, IconButton, Tooltip } from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import CheckIcon from '@material-ui/icons/Check'
import CloseIcon from '@material-ui/icons/Close'
import InfoIcon from '@material-ui/icons/Info'
import { makeStyles } from '@material-ui/styles'
import { useSnackbar } from 'notistack'
import React, { useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'
import { useTranslation } from 'react-i18next'
import { Redirect } from 'react-router-dom'
import HeadTitle from '../../components/HeadTitle/HeadTitle'
import DataTable from '../../components/NewDataTable/NewDataTable'
import ROLES from '../../constants/roles'
import { recoverAccountList, toggleAccountState } from '../../containers/DataContainer'
import {
	useIEEInfoForm,
	useOtherUserInfoForm,
	useSelectUserProfileForm,
	useToggleDOForm,
} from '../../containers/FormContainer'
import Access from '../../containers/RoleContainer'
import { PLATFORM_ID_COOKIE, ACTIVE_ACCOUNT_TYPE_COOKIE } from '../../constants/cookies'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { COLUMNS_USERS, getIndexOfColumn } from '../../utils/Datatable'

const useStyles = makeStyles((theme) => ({
	fab: {
		position: 'fixed',
		bottom: theme.spacing.unit * 2,
		right: theme.spacing.unit * 2,
		zIndex: 1,
	},
	dataTable: {
		width: '95%',
		marginLeft: '2.5%',
		marginBottom: 60,
	},
	link: {
		color: theme.palette.secondary.main,
	},
	icon: {
		color: theme.palette.secondary.main,
	},
	tooltip: {
		display: 'block',
	},
}))

const Users = () => {
	const classes = useStyles()
	const [redirect] = useState()
	const [cookies] = useCookies()
	const otherUserInfoForm = useOtherUserInfoForm()
	const [result, setResult] = useState({ columns: [], data: [], options: {} })
	const ieeInfoForm = useIEEInfoForm()
	const { t } = useTranslation('user')
	const { enqueueSnackbar } = useSnackbar()
	const [listDO, setListDo] = useState({})
	const toggleDOForm = useToggleDOForm(listDO)
	const [refresh, setRefresh] = useState(false)
	const [isLoading, setIsLoading] = useState(true)
	const platformName = useSelector((state) => {
		const find = state.platforms.platforms.find(
			(p) => p.platformId === +cookies[PLATFORM_ID_COOKIE],
		)
		return find ? find.name : ''
	})
	const isAdminOrManager = cookies[ACTIVE_ACCOUNT_TYPE_COOKIE]?.code === 'ADMIN' || 
		cookies[ACTIVE_ACCOUNT_TYPE_COOKIE]?.code === 'PLATFORMMANAGER'

	// Fonction personnalisée pour l'export CSV
	const customDownload = (buildHead, buildBody, p_Columns, p_Data) => {
		// Convertir les noms des colonnes en labels
		p_Columns.map((column) => (column.name = column.label))
		
		// Trouver l'index de la colonne propriétaire et identifiant
		let ownerIndex = p_Columns.findIndex((column) => 
			column.name.toLowerCase() === 'propriétaire' || 
			column.name.toLowerCase() === 'owner' ||
			column.name.toLowerCase().includes('propriétaire')
		)
		
		let loginIndex = p_Columns.findIndex((column) => 
			column.name.toLowerCase() === 'identifiant' || 
			column.name.toLowerCase() === 'login' ||
			column.name.toLowerCase().includes('identifiant') ||
			column.name.toLowerCase().includes('login')
		)
		
		// NE PAS supprimer la colonne propriétaire maintenant - on en a besoin pour la correspondance
		// La suppression se fera après avoir ajouté les nouvelles colonnes
		
		// Déplacer la colonne "Identifiant" en première position
		if (loginIndex !== -1 && loginIndex !== 0) {
			const loginColumn = p_Columns.splice(loginIndex, 1)[0]
			p_Columns.unshift(loginColumn)
			
			p_Data.forEach((row) => {
				const loginValue = row.data.splice(loginIndex, 1)[0]
				row.data.unshift(loginValue)
			})
			
			// Mettre à jour l'index de owner après le déplacement
			if (ownerIndex > loginIndex) {
				ownerIndex -= 1
			} else if (ownerIndex < loginIndex) {
				ownerIndex += 1 // car on a ajouté un élément au début
			}
		}
		
		// Supprimer la colonne actions AVANT d'ajouter les nouvelles colonnes
		let actionIndex = p_Columns.findIndex((object) => {
			return object.name === 'Action' || object.name === 'actions' || 
			       object.name.toLowerCase().includes('action') ||
			       object.name.toLowerCase().includes('état') ||
			       object.name.toLowerCase().includes('etat')
		})
		if (actionIndex !== -1) {
			p_Columns.splice(actionIndex, 1)
			p_Data.forEach((row) => {
				row.data.splice(actionIndex, 1)
			})
		}
		
		// Créer les nouvelles colonnes
		const nomColumn = { name: 'Nom', label: 'Nom' }
		const prenomColumn = { name: 'Prénom', label: 'Prénom' }
		const nomEntrepriseColumn = { name: 'Nom Entreprise', label: 'Nom Entreprise' }
		const nomAgenceColumn = { name: 'Nom Agence', label: 'Nom Agence' }
		const raisonSocialColumn = { name: 'Raison Social', label: 'Raison Social' }
		
		// Insérer après "Identifiant" - Nom, Prénom, puis les 3 nouvelles colonnes
		p_Columns.splice(1, 0, nomColumn, prenomColumn, nomEntrepriseColumn, raisonSocialColumn, nomAgenceColumn)

		// Ajouter les données pour chaque ligne
		p_Data.forEach((row, rowIndex) => {
			let nom = ''
			let prenom = ''
			let nomEntreprise = ''
			let nomAgence = ''
			let raisonSocial = ''
			
			// Utiliser la combinaison propriétaire + identifiant pour faire la correspondance
			// cas spécial : si l'identifiant est "Profil TotalEnergies", utiliser seulement le propriétaire
			const ownerValue = ownerIndex !== -1 ? row.data[ownerIndex] : null
			const loginValue = row.data[0] // Identifiant est maintenant en position 0
			
			let originalData = null
			if (result.data) {
				if (loginValue === "Profil TotalEnergies") {
					// Cas spécial : correspondance uniquement par propriétaire
					originalData = result.data.find(item => item.owner === ownerValue)
				} else {
					// Cas normal : correspondance par propriétaire + identifiant
					originalData = result.data.find(item => item.owner === ownerValue && item.login === loginValue)
				}
			}
			
			if (originalData) {
				// Extraction simple et directe des propriétés
				nom = originalData.userLastName || ''
				prenom = originalData.userFirstName || ''
				nomEntreprise = originalData.companyName || ''
				nomAgence = originalData.companyAgence || ''
				raisonSocial = originalData.socialReason || ''
			}
			
			// Insérer toutes les nouvelles données (positions 1 à 5)
			row.data.splice(1, 0, nom, prenom, nomEntreprise, nomAgence, raisonSocial)
		})
		
		// MAINTENANT supprimer la colonne "Propriétaire" après avoir utilisé les données
		// Mettre à jour l'index car on a ajouté 5 colonnes
		if (ownerIndex !== -1) {
			const newOwnerIndex = ownerIndex > 0 ? ownerIndex + 5 : ownerIndex // +5 car on a ajouté 5 colonnes après la position 1
			p_Columns.splice(newOwnerIndex, 1)
			p_Data.forEach((row) => {
				row.data.splice(newOwnerIndex, 1)
			})
		}

		// CONSTRUCTION MANUELLE DU CSV
		// Construire l'en-tête manuellement avec des points-virgules
		const csvHeader = p_Columns.map(col => `"${col.name}"`).join(';')
		
		// Construire les lignes de données manuellement avec des points-virgules
		const csvRows = p_Data.map(row => {
			return row.data.map(cell => {
				// Échapper les guillemets et encapsuler dans des guillemets
				const cellStr = String(cell || '').replace(/"/g, '""')
				return `"${cellStr}"`
			}).join(';')
		})
		
		// Assembler le CSV complet
		const fullCSV = [csvHeader, ...csvRows].join('\n')
		
		// Encodage correct pour l'export CSV
		return '\uFEFF' + fullCSV
	}

	useEffect(() => {
		setIsLoading(true)
		recoverAccountList(
			{ platformId: cookies[PLATFORM_ID_COOKIE], nbItemsAsked: 10 },
			function (res, err) {
				if (err) return console.error(err)
				if (!res) return console.error('No response !')
				setResult(res.data)
				setIsLoading(false)
			},
		)
	}, [refresh])

	const OpenInfoForm = () => {
		ieeInfoForm.open()
	}

	const OpenOtherUserForm = () => {
		otherUserInfoForm.open()
	}

	const MatchGuidRegex = (strIn) => {
		const regexGuid =
			/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/
		return regexGuid.test(strIn)
	}

	const { formular, open } = useSelectUserProfileForm({ OpenInfoForm, OpenOtherUserForm })

	const accountInfo = (companyId) => {
		window.open('/op-infos/' + companyId)
	}

	const toggleAccount = (accountId) => {
		toggleAccountState(
			{
				platformId: cookies[PLATFORM_ID_COOKIE],
				accountId: accountId,
			},
			(res, error) => {
				if (error) {
					console.error(error)
					if (error.data) {
						setRefresh(!refresh)
						return enqueueSnackbar(t(error.data[0].code), { variant: 'error' })
					}
					setRefresh(!refresh)
					return enqueueSnackbar(t('error') + ` : ${error.status} ! `, { variant: 'error' })
				}
				if (res) {
					if (res.data && res.data.message === 'Succeeded') {
						if (res.data.statusCode && res.data.statusCode === 'DOSubstitate') {
							setListDo({ listDO: res.data.doDict, accountId: accountId })
							toggleDOForm.open()
							return
						}
						enqueueSnackbar(t('AccountStatusHasBeenChanged'), { variant: 'info' })
						setRefresh(!refresh)
						return
					}
					setRefresh(!refresh)
					enqueueSnackbar(t('AccountStatusHasBeenChanged'), { variant: 'info' })
				}
			},
		)
	}

	const ToggleAccountButton = ({ accountId, mode }) => {
		const [initialMode, setInitialMode] = useState()

		useEffect(() => {
			setInitialMode(mode)
		}, [mode])

		return (
			<Access accept={[ROLES.ADMIN, ROLES.PLATFORMMANAGER]}>
				<Tooltip title={initialMode ? t('DisableAccount') : t('ActivateAccount')}>
					<IconButton
						size="small"
						className={classes.icon}
						onClick={() => {
							toggleAccount(accountId)
							setInitialMode(!initialMode)
						}}
					>
						{initialMode && <CloseIcon />}
						{!initialMode && <CheckIcon />}
					</IconButton>
				</Tooltip>
			</Access>
		)
	}
	ToggleAccountButton.propTypes = {
		accountId: PropTypes.number,
		mode: PropTypes.bool,
	}

	const AddUser = () => (
		<Fab className={classes.fab} color="secondary" onClick={open}>
			<AddIcon />
		</Fab>
	)

	return (
		<>
			{formular}
			{ieeInfoForm.formular}
			{otherUserInfoForm.formular}
			{toggleDOForm.formular}
			{redirect && <Redirect to={redirect} />}
			<HeadTitle title={t('UsersList')} link="/dashboard" />
			<DataTable
				title={t('UsersList')}
				columns={result.columns}
				data={result.data}
				options={{
					...result.options,
					onDownload: customDownload
				}}
				className={classes.dataTable}
				isLoading={isLoading}
				defaultFilters={[
					{
						name: 'platforms',
						value: [platformName],
					},
					{
						name: 'isActive',
						value: [t('users_isActive')],
					},
				]}
				customColumns={[
					{
						name: 'info',
						component: (value) => {
							if (value)
								return (
									<Access
										accept={[
											ROLES.ADMIN,
											ROLES.PLATFORMMANAGER,
											ROLES.PRINCIPAL,
											ROLES.INTERNALOBS,
										]}
									>
										<Tooltip title={t('AccessAccountInformation')}>
											<IconButton size="small" onClick={() => accountInfo(value)}>
												<InfoIcon className={classes.icon} />
											</IconButton>
										</Tooltip>
									</Access>
								)
						},
					},
					{
						name: 'state',
						component: function componentToggleAccountButton(value, tableMeta) {
							if (!tableMeta.rowData[getIndexOfColumn(result.columns, COLUMNS_USERS.PLATFORMS)])
								return
							return (
								<>
									<ToggleAccountButton
										accountId={
											tableMeta.rowData[getIndexOfColumn(result.columns, COLUMNS_USERS.ID)]
										}
										mode={value}
									/>
								</>
							)
						},
					},
					{
						name: 'isActive',
						component: (value) => {
							if (!value) return
							return t('users_isActive')
						},
					},
					{
						name: 'login',
						component: function componentAccess(value, tableMeta) {
							const userId = tableMeta.rowData[getIndexOfColumn(result.columns, COLUMNS_USERS.ID)]
							// Pour le filtrage et l'affichage des utilisateurs sans identifiant
							if (value == null) {
								const displayText = t('interne')
								tableMeta.rowData[getIndexOfColumn(result.columns, COLUMNS_USERS.LOGIN)] = 'Profil TotalEnergies'
								return (
									<>
										{isAdminOrManager && (
											<a
												href={'myAccount/' + userId}
												rel="noopener noreferrer"
												className={classes.link}
											>
												{displayText}
											</a>
										)}
										{!isAdminOrManager && <>{displayText}</>}
									</>
								)
							}
							if (MatchGuidRegex(value)) {
								return (
									<>
										{isAdminOrManager && (
											<Tooltip title={t('AccountCreationPending')}>
												<a
													href={'myAccount/' + userId}
													rel="noopener noreferrer"
													className={classes.link}
												>
													{value}
												</a>
											</Tooltip>
										)}
										{!isAdminOrManager && ROLES.PRINCIPAL && <> {value} </>}
									</>
								)
							}
							return (
								<>
									{isAdminOrManager && (
										<a
											href={'myAccount/' + userId}
											rel="noopener noreferrer"
											className={classes.link}
										>
											{value}
										</a>
									)}
									{!isAdminOrManager && ROLES.PRINCIPAL && <> {value} </>}
								</>
							)
						},
					},
				]}
				customLabels={[
					{
						name: 'owner',
						label: t('owner'),
					},
					{
						name: 'phone-number',
						label: t('phone-number'),
					},
					{
						name: 'login',
						label: t('login'),
					},
					{
						name: 'email',
						label: t('email'),
					},
					{
						name: 'function',
						label: t('function'),
					},
					{
						name: 'state',
						label: t('state'),
					},
					{
						name: 'info',
						label: t('info'),
					},
					{
						name: 'platforms',
						label: t('platforms'),
					},
					{
						name: 'siret',
						label: t('siretNumber'),
					},
				]}
			/>

			<Access accept={[ROLES.ADMIN, ROLES.PLATFORMMANAGER]}>
				<AddUser className={classes.fab} />
			</Access>
		</>
	)
}
export default Users
